import page from "@/core/page"
import parseAggregateRatings from "@/metadata/product/ratings/aggregateRating"
import parsePrice from "@/metadata/utils/price"
import text from "@/metadata/utils/text"
import innerHtml from "@/metadata/utils/innerHtml"
import { schemaFn, select, selectMulti, keyval } from "@/metadata/utils/schema"
import linkify from "@/metadata/product/url/linkify"
import { PushedProductSKU, PushedVariation } from "@/types"
import emptyToUndefined from "@/metadata/utils/empty"
import pipe from "@/utils/pipe"
import texts from "../utils/texts"
import toFloat from "../utils/toFloat"
import toInt from "../utils/toInt"
import { Product } from "@/core/tagging/types"
import settings from "@/core/settings"
import context from "@/core/context"

function mop(values: string[]) {
  return values ? emptyToUndefined(values.filter(value => value.trim().length)) : undefined
}

const skuFn = schemaFn<PushedProductSKU>({
  id: select(".id"),
  name: select(".name"),
  price: select(".price", pipe(text, parsePrice)),
  list_price: select(".list_price", pipe(text, parsePrice)),
  url: select(".url"),
  image_url: select(".image_url"),
  gtin: select(".gtin"),
  availability: select(".availability"),
  custom_fields: node => {
    return emptyToUndefined(keyval(".custom_fields > *")(node))!
  }
})

const variationFn = schemaFn<Partial<PushedVariation> & { variation_id: string }>({
  variation_id: select(".variation_id"),
  price: select(".price", pipe(text, parsePrice)),
  list_price: select(".list_price", pipe(text, parsePrice)),
  price_currency_code: select(".price_currency_code"),
  availability: select(".availability")
})

const productFn = schemaFn<Product>({
  url: select(
    ":scope > .url",
    pipe(text, (v: string | undefined) => v || linkify())
  ),
  product_id: select(":scope > .product_id"),
  selected_sku_id: select(":scope > .selected_sku_id"),
  name: select(":scope > .name"),
  image_url: select(":scope > .image_url"),
  thumb_url: select(":scope > .thumb_url"),
  price: select(":scope > .price", pipe(text, parsePrice)),
  list_price: select(":scope > .list_price", pipe(text, parsePrice)),
  price_currency_code: select(":scope > .price_currency_code"),
  availability: select(":scope > .availability"),
  brand: select(":scope > .brand"),
  description: select(":scope > .description", innerHtml),
  // @ts-expect-error field mismatch
  date_published: select(":scope > .date_published"),
  valid_until: select(":scope > .valid_until"),
  variation_id: select(":scope > .variation_id"),
  review_count: select(":scope > .review_count", pipe(text, toInt)),
  rating_value: select(":scope > .rating_value", pipe(text, toFloat)),
  condition: select(":scope > .condition"),
  gender: select(":scope > .gender"),
  age_group: select(":scope > .age_group"),
  gtin: select(":scope > .gtin"),
  google_category: select(":scope > .google_category"),
  unit_pricing_measure: select(":scope > .unit_pricing_measure", pipe(text, toFloat)),
  unit_pricing_base_measure: select(":scope > .unit_pricing_base_measure", pipe(text, toFloat)),
  unit_pricing_unit: select(":scope > .unit_pricing_unit"),
  tags1: selectMulti(":scope > .tags1 > .tag, :scope > .tag1", pipe(texts, mop)),
  tags2: selectMulti(":scope > .tags2 > .tag, :scope > .tag2", pipe(texts, mop)),
  tags3: selectMulti(":scope > .tags3 > .tag, :scope > .tag3", pipe(texts, mop)),
  alternate_image_urls: selectMulti(
    ":scope > .alternate_image_urls > .alternate_image_url, :scope > .alternate_image_url",
    pipe(texts, mop)
  ),
  category: selectMulti(":scope > .category, :scope > .categories > .category", pipe(texts, mop)),
  category_id: selectMulti(":scope > .category_id, :scope > .category_ids > .category_id", pipe(texts, mop)),
  parent_category_id: selectMulti(":scope > .parent_category_ids > .parent_category_id", pipe(texts, mop)),
  custom_fields: pipe(keyval(":scope > .custom_fields > *"), emptyToUndefined),
  skus: node => {
    const elements = Array.from(node.querySelectorAll<HTMLElement>(":scope .nosto_sku"))
    return emptyToUndefined(elements.map(skuFn))
  },
  variations: (node: ParentNode) => {
    const elements = Array.from(
      node.querySelectorAll<HTMLElement>(":scope > .variations > .variation, :scope > .variation")
    )
    const variations = elements.map(variationFn).filter(v => v.variation_id)
    const data = variations.reduce((map, variation) => ({ ...map, [variation.variation_id]: variation }), {})
    return emptyToUndefined(data)
  }
})

export default function findProducts() {
  const pDivs = page.selectAll(".nosto_product")
  const products = Array.from(pDivs).map(productFn).map(parseAggregateRatings)

  // augment product tagging with selected SKU ID from URL
  if (products.length && settings.skuParam) {
    const parameters = context.siteUrl.searchParams
    if (parameters.has(settings.skuParam)) {
      const skuId = parameters.get(settings.skuParam)!
      return products.map(product => ({ ...product, selected_sku_id: skuId }))
    }
  }
  return products
}
